import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface CapabilityMeta {
  tag: string;
  end: number;
  format: (n: number) => string;
}

const capabilityMeta: CapabilityMeta[] = [
  { tag: "workflows", end: 10, format: (n) => `${Math.round(n)}×` },
  { tag: "delivery", end: 3, format: (n) => `${Math.round(n)}×` },
  { tag: "ownership", end: 100, format: (n) => `${Math.round(n)}%` },
  { tag: "agents", end: 24, format: (n) => `${Math.round(n)}/7` },
];

/** Animated stat that counts up once it scrolls into view. */
const CountStat = ({ end, format, started }: { end: number; format: (n: number) => string; started: boolean }) => {
  // Start at the final value so the prerendered/crawler snapshot shows real
  // numbers; the count-up resets to 0 and animates only when scrolled into view.
  const [val, setVal] = useState(end);
  useEffect(() => {
    if (!started) return;
    const start = performance.now();
    const duration = 1600;
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 4);
      setVal(eased * end);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setVal(end);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, end]);
  return <span className="tabular-nums">{format(val)}</span>;
};

const AICapabilityCards = () => {
  const { t } = useTranslation();
  const texts = t("ai.capabilities", { returnObjects: true }) as {
    title: string;
    statLabel: string;
  }[];
  const capabilities = capabilityMeta.map((meta, i) => ({
    ...meta,
    title: texts[i]?.title ?? "",
    statLabel: texts[i]?.statLabel ?? "",
  }));
  const wrapRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          obs.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={wrapRef} className="mb-14">
      {/* Readout header */}
      <div className="flex items-end justify-between border-b border-border pb-4">
        <span className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground/80">[ 04 / 04 ]</span>
      </div>

      {/* Spec-sheet grid — no boxes, hairline dividers */}
      <div className="grid grid-cols-2 lg:grid-cols-4 border-b border-border">
        {capabilities.map((cap, i) => (
          <div
            key={cap.title}
            className="group relative px-4 py-6 sm:px-7 sm:py-8 lg:px-8 border-border max-lg:[&:nth-child(even)]:border-l max-lg:[&:nth-child(n+3)]:border-t max-lg:[&:nth-child(odd)]:pl-0 max-lg:[&:nth-child(even)]:pr-0 lg:[&:not(:first-child)]:border-l lg:first:pl-0 lg:last:pr-0 transition-colors duration-300 hover:bg-accent/[0.03]"
            style={{
              opacity: started ? 1 : 0,
              transform: started ? "translateY(0)" : "translateY(20px)",
              transition: `opacity 600ms cubic-bezier(0.16,1,0.3,1) ${i * 90}ms, transform 600ms cubic-bezier(0.16,1,0.3,1) ${i * 90}ms`,
            }}
          >
            {/* index + tag */}
            <div className="flex items-center gap-2 mb-4 sm:mb-7 font-mono text-[10px] sm:text-[11px] tracking-[0.2em]">
              <span className="text-accent">0{i + 1}</span>
              <span className="text-muted-foreground">/ {cap.tag}</span>
            </div>

            {/* big number */}
            <div className="text-[1.9rem] sm:text-5xl md:text-6xl lg:text-[5rem] font-bold leading-[0.9] tracking-tighter text-foreground">
              <CountStat end={cap.end} format={cap.format} started={started} />
            </div>

            {/* label + title */}
            <p className="mt-2.5 sm:mt-3 text-xs sm:text-sm text-accent">{cap.statLabel}</p>
            <h3 className="mt-1 sm:mt-1.5 text-xs sm:text-sm font-medium text-muted-foreground">{cap.title}</h3>

            {/* hover accent underline */}
            <span className="pointer-events-none absolute bottom-0 left-0 h-px w-0 bg-accent transition-[width] duration-300 group-hover:w-full" />
          </div>
        ))}
      </div>
    </div>
  );
};

export default AICapabilityCards;
